import { Component, Inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { CommonFunctionsService } from 'src/app/services/common-functions.service';
import {
  DataModelService,
  WorkHourEntryAction,
  WorkHourEntryReason,
} from 'src/app/services/data-model.service';
import {
  User,
  WorkHourEntryDialogIntention,
  WorkHourEntryDialogResult,
} from 'src/app/services/definitions.service';
import { LogService } from 'src/app/services/log.service';
import { map, startWith } from 'rxjs/operators';

export interface WorkHourEntryDialogData {
  result: WorkHourEntryDialogResult;
  dialogIntention: WorkHourEntryDialogIntention;
  dialogTitle: string;
  descriptionText: string;
  buttonNoName: string;
  buttonYesName: string;
  comment: string;
  action: WorkHourEntryAction;
  reason: WorkHourEntryReason;
  amount: number;
  maxAmount: number;
  currentUserId: string;
  currentUserName: string;
  destinationUserId: string;
}

@Component({
  selector: 'work-hour-entry',
  templateUrl: 'work-hour-entry.html',
  styleUrls: ['work-hour-entry.css'],
})
export class WorkHourEntryDialog {
  commentFormControl = new FormControl('');
  entryActionFormControl = new FormControl(1, [Validators.required]);
  amountFormControl = new FormControl(WorkHourEntryAction.ADD, [
    Validators.required,
  ]);
  userSelectorFormControl = new FormControl('');
  filteredNameOptions: Observable<User[]>;
  myForm: FormGroup;
  wheA = [WorkHourEntryAction.ADD, WorkHourEntryAction.REMOVE];
  allUsersData: User[];
  options: User[];
  selectedUserData: any;
  isMobile: boolean;
  dataLoaded: boolean;
  userData: User;

  constructor(
    public dialogRef: MatDialogRef<WorkHourEntryDialog>,
    @Inject(MAT_DIALOG_DATA) public data: WorkHourEntryDialogData,
    private dataModel: DataModelService,
    private commonFunctions: CommonFunctionsService,
    private spinnerService: NgxSpinnerService,
    fb: FormBuilder,
    private log: LogService,
    private authService: AuthService
  ) {
    //load current users data
    commonFunctions.registerEventEmitterHandlerUserData((userData) => {
      this.userData = userData;
    });
    this.isMobile =
      data.dialogIntention == WorkHourEntryDialogIntention.ADD_MOBILE;

    if (this.isMobile) {
      //load all users data
      this.spinnerService.show();
      this.commonFunctions.loadAllUsers(
        (res: boolean, allUsersData: User[], options: User[]) => {
          if (res) {
            this.allUsersData = allUsersData;
            this.options = options;

            this.filteredNameOptions =
              this.userSelectorFormControl.valueChanges.pipe(
                startWith(''),
                map((value) => this._filter(value || ''))
              );
          }
          this.spinnerService.hide();
        }
      );
      this.userSelectorFormControl = new FormControl('', [Validators.required]);
      this.myForm = fb.group({
        comment: this.commentFormControl,
        entryAction: this.entryActionFormControl,
        amount: this.amountFormControl,
        userSelector: this.userSelectorFormControl,
      });
    } else {
      this.dataLoaded = true;
      this.myForm = fb.group({
        comment: this.commentFormControl,
        entryAction: this.entryActionFormControl,
        amount: this.amountFormControl,
      });
    }
  }

  //Filter input by name
  private _filter(value: string): User[] {
    if (typeof value == 'string') {
      const filterValue = value.toLowerCase();

      return this.options.filter((option: User) =>
        this.displayFn(option).toLowerCase().includes(filterValue)
      );
    }
  }

  onNoClick(): void {
    this.data.result = 0;
    this.dialogRef.close(this.data);
  }

  onYesClick(): void {
    var comment = this.myForm.get('comment').value;
    var type: WorkHourEntryAction = this.myForm.get('entryAction').value;
    var amount = this.myForm.get('amount').value;
    var destinationUserId = this.data.destinationUserId;
    var entryCreatorId = this.data.currentUserId;
    var entryCreatorName = this.data.currentUserName;

    this.addWorkHour(
      destinationUserId,
      entryCreatorId,
      entryCreatorName,
      comment,
      amount,
      type
    );
  }

  //name selection done
  public optionSelected(event) {
    if (event.isUserInput) {
      var val = event.source.value;
      if (val == WorkHourEntryAction.REMOVE) {
        this.amountFormControl.clearValidators();
        this.amountFormControl.setValidators(
          Validators.max(this.data.maxAmount)
        );
      } else {
        this.amountFormControl.clearValidators();
        this.amountFormControl.setValidators(Validators.required);
      }
      this.amountFormControl.updateValueAndValidity();
    }
  }

  //Fügt einen neuen Arbeitsstunden-Eintrag hinzu
  private addWorkHour(
    destinationUserId: string,
    entryCreatorId: string,
    entryCreatorName: string,
    comment: string = '',
    amount: number,
    addOrRemove: WorkHourEntryAction,
    reason: WorkHourEntryReason = WorkHourEntryReason.STANDARD
  ) {
    this.spinnerService.show();
    this.dataModel
      .createWorkHourEntry(
        destinationUserId,
        amount,
        addOrRemove,
        comment,
        reason,
        entryCreatorId,
        entryCreatorName
      )
      .then(
        (done) => {
          if (done) {
            this.commonFunctions.showSuccessToast(
              'Arbeitsstunden erfolgreich eingetragen',
              1000
            );
            this.data.result = 1;
            this.dialogRef.close(this.data);
          } else {
            this.commonFunctions.showErrorToast(
              'Arbeitsstunden konnte nicht eingetragen werden'
            );
          }
        },
        (rejected) => {
          this.commonFunctions.showErrorToast(
            'Arbeitsstunden konnte nicht eingetragen werden: ' + rejected
          );
        }
      )
      .catch((error) => {
        this.commonFunctions.showErrorToast(
          'Arbeitsstunden konnte nicht eingetragen werden: ' + error
        );
      })
      .finally(() => {
        this.spinnerService.hide();
      });
  }

  //name selection done
  public optionSelectedUser(val: any) {
    this.dataLoaded = false;
    this.loadUserData();
  }

  displayFn(user: User): string {
    if (!user) {
      return '';
    }
    var firstName = user?.personalData?.firstName;
    var lastName = user?.personalData?.lastName;
    var fullName = firstName + ' ' + lastName;
    return fullName && fullName ? fullName : '';
  }

  //Loads the data of the selected user
  private loadUserData() {
    this.spinnerService.show();
    if (!this.allUsersData) {
      this.commonFunctions.showErrorToast(
        'Userdaten konnten nicht geladen werden'
      );
      return;
    }

    var selectedUserId = this.userSelectorFormControl.value?.uid;
    if (selectedUserId == null) {
      this.commonFunctions.showErrorToast('Ungültiger Eintrag ausgewählt');
      return;
    }
    //overwrite in dialog data
    this.data.destinationUserId = selectedUserId;
    this.data.currentUserId = this.authService.userId;
    this.data.currentUserName = this.commonFunctions.getPrettyPrintedUserName(
      this.userData
    );

    //load data of user
    this.dataModel.loadUser(selectedUserId).then(
      (data) => {
        if (data) {
          this.dataLoaded = true;
          this.data.maxAmount = data.sumWorkHours;
        } else {
          this.commonFunctions.showErrorToast('User nicht in DB');
          return;
        }
        this.spinnerService.hide();
      },
      (rejectMsg) => {
        this.log.error(
          'Userdaten konnten nicht geladen werden. Arbeitsstunden-Anzeige funktioniert folglich nicht',
          rejectMsg,
          WorkHourEntryDialog.name
        );
        this.spinnerService.hide();
      }
    );

    //find selected user id in 'allUsersData'
    this.selectedUserData = this.allUsersData.find((x) => {
      return x.uid == selectedUserId;
    });
  }
}
